import AddressField, {
  AddressFieldImperativeActions,
} from '@otovo/shared/components/Form/AddressField';
import {
  Checkbox,
  CheckboxField,
  CheckboxLabel,
} from '@otovo/shared/components/Form/CheckboxField';
import {
  EmailField,
  FirstNameField,
  LastNameField,
} from '@otovo/shared/components/Form/CommonFields';
import { Description } from '@otovo/shared/components/Form/FormElements';
import ValidatedPhoneNumberInput, {
  ValidatedPhoneNumberImperativeActions,
} from '@otovo/shared/components/Form/ValidatedPhoneNumberInput/ValidatedPhoneNumberInput';
import useCreateInstantActivation from '@otovo/shared/components/InterestRegistration/AddressSearch/useCreateInstantActivation';
import PortableText from '@otovo/shared/components/Sanity/PortableTextComponents';
import { useGlobalConfig } from '@otovo/shared/hooks/configContext';
import {
  logActivation,
  logInterest,
} from '@otovo/shared/lib/analytics/analytics';
import Button from '@otovo/shared/rainbow/Button/Button';
import { Sanity$OtovoInstantActivationForm } from '@otovo/shared/types/sanityTypes';
import { stegaClean } from 'next-sanity';
import React, { Fragment, useRef, useState } from 'react';

type Props = {
  content: Sanity$OtovoInstantActivationForm;
};

const InstantActivationForm = ({ content }: Props) => {
  const config = useGlobalConfig();
  const { createInstantActivation, isCreatingInstantActivation } =
    useCreateInstantActivation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const phoneNumberRef = useRef<ValidatedPhoneNumberImperativeActions>(null);
  const addressRef = useRef<AddressFieldImperativeActions>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isCreatingInstantActivation) return;

    setSubmitError(null);

    try {
      // Validate phone number and address
      const [phoneNumber, addressId] = await Promise.all([
        phoneNumberRef.current?.validatePhoneNumber(),
        addressRef.current?.validateAddress(),
      ]);

      if (!phoneNumber) {
        phoneNumberRef.current?.setIsRequired();
      }
      if (!addressId) {
        addressRef.current?.setIsRequired();
      }

      if (!phoneNumber || !addressId) {
        return;
      }

      // Get form data using HTML form validation
      const formData = new FormData(e.target as HTMLFormElement);
      const firstName = formData.get('first_name') as string;
      const lastName = formData.get('last_name') as string;
      const email = formData.get('email') as string;
      const consent = formData.get('consent') === 'on';
      const consent_text = formData.get('consent_text') as string;

      if (!firstName?.trim() || !lastName?.trim() || !email?.trim()) {
        throw new Error(content.partialFormError);
      }

      const response = await createInstantActivation({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        address: addressId,
        email: email.trim(),
        phone_number: phoneNumber,
        consent: consent,
        consent_text: stegaClean(consent_text),
        locale: config.BU_CONFIG.locale,
        mainHardwareType: 'pv_system',
      });

      // Log conversion metrics
      logInterest({ locale: config.BU_CONFIG.locale });

      await logActivation({
        interestId: response.interestId,
        financingOption: undefined, // No financing option for instant activation
        config,
        email: email.trim(),
        phoneNumber: phoneNumber,
        address: response.address,
        name: `${firstName.trim()} ${lastName.trim()}`,
      });

      setIsSuccess(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error && Number.isNaN(Number(error.message))
          ? error.message
          : `${content.invalidStatusResponse} ${error.message}`,
      );
    }
  };

  const isFormDisabled = isSuccess || isCreatingInstantActivation;

  return (
    <div
      className="relative"
      data-testid={content.sectionId}
      id={content.sectionId}
    >
      <div className="color-scheme-light bg-surface py-6">
        <div className="mx-auto max-w-8xl px-6 py-16 md:py-20 lg:px-8 lg:py-24">
          <div className="color-scheme-dark mx-auto max-w-2xl rounded-lg bg-surface shadow-md lg:flex lg:max-w-none">
            <div className="flex flex-grow flex-col p-8 sm:p-10">
              {/*{content.trustVendor && (
                <div className="mb-3">
                  <TrustVendor {...content.trustVendor} />
                </div>
              )}*/}
              <div className="lg:mr-20">
                <h2 className="text-2xl font-semibold text-balance break-words text-heading md:text-3xl lg:mr-16 lg:text-3xl">
                  {content.sectionHeading}
                </h2>
                <p className="mt-4 text-base text-subheading">
                  {content.sectionLead}
                </p>
                <h3 className="mt-10 text-xl font-semibold text-subheading">
                  {content.contentTitle}
                </h3>

                <PortableText value={content.content} />
              </div>

              <div className="mt-auto hidden items-center justify-between gap-x-5 text-sm font-normal text-body/60 lg:flex">
                {(content.valueProps || []).map((valueProp, index, arr) => (
                  <Fragment key={valueProp}>
                    <span className="flex-grow text-center text-balance">
                      {valueProp}
                    </span>
                    {index !== arr.length - 1 && (
                      <span className="text-xl font-semibold">&middot;</span>
                    )}
                  </Fragment>
                ))}
              </div>
            </div>
            <form
              onSubmit={handleSubmit}
              className="color-scheme-white -m-2 px-2 py-0 lg:w-full lg:max-w-[50%] lg:shrink-0 lg:py-2"
            >
              <div className="h-full rounded-b-lg bg-surface pt-10 pb-5 inset-ring inset-ring-white/10 lg:-mr-px lg:flex lg:flex-col lg:justify-center lg:rounded-r-lg lg:rounded-bl-none lg:pt-16 lg:pb-10">
                <div className="mx-auto w-full max-w-lg px-3 xl:px-0">
                  <div className="flex gap-4">
                    <FirstNameField
                      disabled={isFormDisabled}
                      className="mt-4 flex-auto"
                    />
                    <LastNameField
                      disabled={isFormDisabled}
                      className="mt-4 flex-auto"
                    />
                  </div>

                  <div className="mt-4">
                    <ValidatedPhoneNumberInput
                      disabled={isFormDisabled}
                      required
                      ref={phoneNumberRef}
                      defaultMarket={config.BU_CONFIG.market}
                    />
                  </div>

                  <EmailField disabled={isFormDisabled} className="mt-4" />

                  <AddressField
                    className="mt-4"
                    disabled={isFormDisabled}
                    ref={addressRef}
                    required
                    locale={config.BU_CONFIG.locale}
                    mainHardwareType="pv_system"
                  />

                  {content.consentText && (
                    <>
                      <CheckboxField className="mt-6">
                        <Checkbox disabled={isFormDisabled} name="consent" />
                        <div>
                          <CheckboxLabel className="w-full text-sm text-body">
                            {content.consentText}
                          </CheckboxLabel>
                          <Description className="mt-1" as="div">
                            <PortableText
                              value={content.consentDescription}
                              components={{
                                marks: {
                                  link: ({ children, value }) => (
                                    // eslint-disable-next-line react/jsx-no-target-blank
                                    <a
                                      tabIndex={-1}
                                      href={value.href}
                                      target="_blank"
                                      rel={value.rel || 'noopener noreferrer'}
                                      className="font-semibold underline hover:text-body"
                                    >
                                      {children}
                                    </a>
                                  ),
                                },
                                block: {
                                  normal: ({ children }) => (
                                    <p className="text-xs text-muted">
                                      {children}
                                    </p>
                                  ),
                                },
                              }}
                            />
                          </Description>
                        </div>
                      </CheckboxField>
                      <input
                        type="hidden"
                        name="consent_text"
                        value={content.consentText}
                      />
                    </>
                  )}

                  {isSuccess ? (
                    <div className="mt-6 rounded-md border border-green-70 bg-green-10 p-4 text-left">
                      <h4 className="mb-1 font-semibold text-green-80">
                        {content.successTitle}
                      </h4>
                      <p className="text-sm font-medium text-green-70">
                        {content.successDetails}
                      </p>
                    </div>
                  ) : (
                    <Button
                      type="submit"
                      className="mt-6 w-full"
                      loading={isCreatingInstantActivation}
                    >
                      {content.submitButton}
                    </Button>
                  )}

                  {submitError && (
                    <div className="mt-4 rounded-md border border-red-20 bg-red-10 p-3">
                      <p className="text-sm text-red-80">{submitError}</p>
                    </div>
                  )}
                  {content.disclaimer && (
                    <p className="mt-3 text-xs text-muted">
                      {content.disclaimer}
                    </p>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstantActivationForm;
